import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Toggle from './Toggle';
import {
  HOME_ROUTE,
  LOGIN_ROUTE,
  SEARCH_MOVIE_ROUTE,
  POPULAR_MOVIES,
  POPULAR_MOVIES_ROUTE,
  TOP_RATED_MOVIES,
  TOP_RATED_MOVIES_ROUTE,
  UPCOMING_MOVIES,
  UPCOMING_MOVIES_ROUTE,
  SEARCH_MOVIE,
} from '../../utils/constants';
import { RootState } from '../../store/appStore';
import Profile from './Profile';
import { useAppSelector } from '../../utils/hooks';

const Header = () => {
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const { userToken, userName, userEmail } = useAppSelector(
    (store: RootState) => store.user
  );
  const location = useLocation();
  const { pathname } = location;
  const selectedLocationClasses =
    'block py-2 px-3 text-white bg-blue-700 rounded md:bg-transparent md:text-blue-700 md:p-0 md:dark:text-blue-500 ml-6';
  const nonSelectedLocationClasses =
    'block py-2 px-3 text-gray-900 rounded hover:bg-gray-100 md:hover:bg-transparent md:hover:text-blue-700 md:p-0 md:dark:hover:text-blue-500 dark:text-white dark:hover:bg-gray-700 dark:hover:text-white md:dark:hover:bg-transparent dark:border-gray-700 ml-6';
  const selectedLocationMobileClasses =
    'block py-2 px-3 text-white bg-blue-700 rounded dark:bg-blue-600';
  const nonSelectedLocationMobileClasses =
    'block py-2 px-3 text-gray-900 rounded hover:bg-gray-300 dark:text-gray-400 md:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white';

  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
  };
  const scrollToTopForMobile = () => {
    window.scrollTo({ top: 0 });
    setShowMobileMenu(false);
  };
  return (
    <header className='fixed -top-0 w-full bg-gray-100 dark:bg-slate-900 z-20'>
      <nav
        className='mx-auto flex max-w-full items-center justify-between p-6 xl:px-8'
        aria-label='Global'>
        <div className='flex items-center'>
          <Link
            to={HOME_ROUTE}
            className='-m-1.5 p-1.5 flex items-center'
            data-testid='home-route'
            onClick={scrollToTop}>
            <img className='h-12 w-12' src='/movie-review.png' alt='logo' />
            <span className='ml-3 text-xl text-slate-900 dark:text-white xl:border-r-2 xl:border-gray-700 xl:pr-4'>
              Movie Review Specialist
            </span>
          </Link>
          <div className='hidden xl:flex xl:gap-x-6'>
            <Link
              to={POPULAR_MOVIES_ROUTE}
              onClick={scrollToTop}
              className={
                pathname === POPULAR_MOVIES_ROUTE
                  ? selectedLocationClasses
                  : nonSelectedLocationClasses
              }>
              {POPULAR_MOVIES.value}
            </Link>
          </div>
          <div className='hidden xl:flex xl:gap-x-6'>
            <Link
              to={TOP_RATED_MOVIES_ROUTE}
              onClick={scrollToTop}
              className={
                pathname === TOP_RATED_MOVIES_ROUTE
                  ? selectedLocationClasses
                  : nonSelectedLocationClasses
              }>
              {TOP_RATED_MOVIES.value}
            </Link>
          </div>
          <div className='hidden xl:flex xl:gap-x-6'>
            <Link
              to={UPCOMING_MOVIES_ROUTE}
              onClick={scrollToTop}
              className={
                pathname === UPCOMING_MOVIES_ROUTE
                  ? selectedLocationClasses
                  : nonSelectedLocationClasses
              }>
              {UPCOMING_MOVIES.value}
            </Link>
          </div>
          <div className='hidden xl:flex xl:gap-x-6'>
            <Link
              to={SEARCH_MOVIE_ROUTE}
              onClick={scrollToTop}
              className={
                pathname === SEARCH_MOVIE_ROUTE
                  ? selectedLocationClasses
                  : nonSelectedLocationClasses
              }>
              {SEARCH_MOVIE.value}
            </Link>
          </div>
        </div>
        <div className='flex xl:hidden'>
          <button
            type='button'
            data-testid='mobile-menu'
            className='-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-white'
            onClick={() => setShowMobileMenu(true)}>
            <span className='sr-only'>Open main menu</span>
            <svg
              className='h-6 w-6'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth='1.5'
              stroke='currentColor'
              aria-hidden='true'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5'
              />
            </svg>
          </button>
        </div>
        <div className='hidden xl:flex xl:flex-1 xl:justify-end items-center'>
          {userToken ? (
            <Profile userName={userName} userEmail={userEmail} />
          ) : (
            <Link
              to={LOGIN_ROUTE}
              onClick={scrollToTop}
              className={
                pathname === LOGIN_ROUTE
                  ? selectedLocationClasses
                  : nonSelectedLocationClasses
              }>
              Login
            </Link>
          )}
          <Toggle />
        </div>
      </nav>

      <div
        className={!showMobileMenu ? 'hidden' : 'xl:hidden'}
        role='dialog'
        aria-modal='true'>
        <div className='fixed inset-0 z-10'></div>
        <div className='fixed inset-y-0 right-0 z-30 w-full overflow-y-auto bg-gray-100 dark:bg-slate-900 px-6 py-6 xl:max-w-sm xl:ring-1 xl:ring-gray-900/10 h-screen'>
          <div className='flex items-center justify-between'>
            <Link
              to={HOME_ROUTE}
              data-testid='home-mobile-route'
              onClick={scrollToTopForMobile}
              className='-m-1.5 p-1.5 flex items-center'>
              <img className='h-12 w-auto' src='/movie-review.png' alt='logo' />
              <span className='ml-3 text-xl text-slate-900 dark:text-white'>
                Movie Review Specialist
              </span>
            </Link>
            <button
              type='button'
              className='-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-white'
              onClick={scrollToTopForMobile}>
              <span className='sr-only'>Close menu</span>
              <svg
                className='h-6 w-6'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth='1.5'
                stroke='currentColor'
                aria-hidden='true'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>
          <div className='mt-6 flow-root'>
            <div className='-my-6 divide-y divide-gray-500/10'>
              <div className='space-y-2 py-6'>
                <Link
                  to={POPULAR_MOVIES_ROUTE}
                  onClick={scrollToTopForMobile}
                  className={
                    pathname === POPULAR_MOVIES_ROUTE
                      ? selectedLocationMobileClasses
                      : nonSelectedLocationMobileClasses
                  }>
                  {POPULAR_MOVIES.value}
                </Link>
                <Link
                  to={TOP_RATED_MOVIES_ROUTE}
                  onClick={scrollToTopForMobile}
                  className={
                    pathname === TOP_RATED_MOVIES_ROUTE
                      ? selectedLocationMobileClasses
                      : nonSelectedLocationMobileClasses
                  }>
                  {TOP_RATED_MOVIES.value}
                </Link>
                <Link
                  to={UPCOMING_MOVIES_ROUTE}
                  onClick={scrollToTopForMobile}
                  className={
                    pathname === UPCOMING_MOVIES_ROUTE
                      ? selectedLocationMobileClasses
                      : nonSelectedLocationMobileClasses
                  }>
                  {UPCOMING_MOVIES.value}
                </Link>
                <Link
                  to={SEARCH_MOVIE_ROUTE}
                  onClick={scrollToTopForMobile}
                  className={
                    pathname === SEARCH_MOVIE_ROUTE
                      ? selectedLocationMobileClasses
                      : nonSelectedLocationMobileClasses
                  }>
                  {SEARCH_MOVIE.value}
                </Link>
                {userToken ? (
                  <Profile
                    userName={userName}
                    userEmail={userEmail}
                    setShowMobileMenu={setShowMobileMenu}
                  />
                ) : (
                  <Link
                    to={LOGIN_ROUTE}
                    onClick={scrollToTopForMobile}
                    className={
                      pathname === LOGIN_ROUTE
                        ? selectedLocationMobileClasses
                        : nonSelectedLocationMobileClasses
                    }>
                    Login
                  </Link>
                )}
              </div>
              <div className='py-6'>
                <Toggle />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
